import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Avatar,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useUserManagement } from '../context/UserManagementContext';
import { userManagementService, User } from '../services/userManagementService';
import { TrustScoreSection } from './TrustScoreSection';

interface UserEditSidebarProps {
  open: boolean;
  onClose: () => void;
  onSave: (user: User, updates: Partial<User>) => Promise<{ success: boolean; message: string }>;
}

export function UserEditSidebar({ open, onClose, onSave }: UserEditSidebarProps) {
  const { state } = useUserManagement();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'PATIENT' as 'PATIENT' | 'CAREGIVER' | 'ADMIN',
    disabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Reset form when user changes
  useEffect(() => {
    if (state.editingUser) {
      setFormData({
        firstName: state.editingUser.firstName || '',
        lastName: state.editingUser.lastName || '',
        email: state.editingUser.email,
        role: state.editingUser.role,
        disabled: state.editingUser.disabled,
      });
      setError(null);
      setSuccess(null);
    }
  }, [state.editingUser]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!state.editingUser) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const updates: Partial<User> = {
        firstName: formData.firstName || null,
        lastName: formData.lastName || null,
      };

      const result = await onSave(state.editingUser, updates);
      
      if (result.success) {
        setSuccess(result.message);
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (newRole: 'PATIENT' | 'CAREGIVER' | 'ADMIN') => {
    if (!state.editingUser) return;

    setLoading(true);
    setError(null);

    try {
      const result = await userManagementService.updateUserRole(state.editingUser.id, newRole);
      if (result.success) {
        setFormData(prev => ({ ...prev, role: newRole }));
        setSuccess('User role updated successfully');
      } else {
        setError('Failed to update user role');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (disabled: boolean) => {
    if (!state.editingUser) return;

    setLoading(true);
    setError(null);

    try {
      const result = await userManagementService.updateUserStatus(state.editingUser.id, disabled);
      if (result.success) {
        setFormData(prev => ({ ...prev, disabled }));
        setSuccess(`User ${disabled ? 'disabled' : 'enabled'} successfully`);
      } else {
        setError('Failed to update user status');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    if (user.lastName) {
      return user.lastName;
    }
    return user.email;
  };

  const getUserInitials = (user: User) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return user.email.charAt(0).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!state.editingUser) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Trust Score panel + signals table need more horizontal room
      // than the original edit-only sidebar. Clamps to the viewport on
      // narrow screens so the drawer never escapes the page.
      PaperProps={{
        sx: { width: { xs: '100%', sm: 720 }, maxWidth: '100vw' },
      }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h6" component="h2">
            Edit User
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* User Info */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Avatar
            src={state.editingUser.avatar || undefined}
            sx={{
              bgcolor: userManagementService.getRoleColor(state.editingUser.role),
              width: 60,
              height: 60,
            }}
          >
            {getUserInitials(state.editingUser)}
          </Avatar>
          <Box>
            <Typography variant="h6">
              {getUserDisplayName(state.editingUser)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {state.editingUser.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Form */}
        <Box flex={1}>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error">{error}</Alert>
            )}
            
            {success && (
              <Alert severity="success">{success}</Alert>
            )}

            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              fullWidth
              disabled={loading}
            />

            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              fullWidth
              disabled={loading}
            />

            <TextField
              label="Email"
              value={formData.email}
              disabled
              fullWidth
              helperText="Email cannot be changed"
            />

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => handleRoleChange(e.target.value as any)}
                disabled={loading}
              >
                <MenuItem value="PATIENT">Patient</MenuItem>
                <MenuItem value="CAREGIVER">Caregiver</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.disabled}
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value === 'true')}
                disabled={loading}
              >
                <MenuItem value="false">Active</MenuItem>
                <MenuItem value="true">Disabled</MenuItem>
              </Select>
            </FormControl>

            <Divider />

            {/* User Details */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Account Details
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    User ID:
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {state.editingUser.id}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Created:
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(state.editingUser.createdAt)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Last Updated:
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(state.editingUser.updatedAt)}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* FEAT-043-admin-web — admin-only Trust Score panel
                (MVP-8.9, MVP-18.24). Starts collapsed; clicking
                "View trust score" opens the read-reason dialog. Does
                NOT auto-load on mount. */}
            <TrustScoreSection
              userId={state.editingUser.id}
              userDisplayName={getUserDisplayName(state.editingUser)}
              userRole={state.editingUser.role}
            />
          </Stack>
        </Box>

        {/* Actions */}
        <Box mt={3}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              fullWidth
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}
